import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  loadTortus,
  type ConsultationOutput,
  type TortusClient,
  type TortusConsultation,
  type TortusConsultationData,
  type TortusError,
} from "@tortus-ai/embed-client";
import type { Promisable } from "type-fest";

import { config } from "../../../config.js";

/**
 * Token data returned by aspire-server's launch-token endpoint. Declared here
 * rather than taken from the shared types so stories and tests can build fake
 * providers against a local shape.
 */
export type TortusAvtLaunchToken = {
  launchToken: string;
  tortusUserId: string;
  expiresInSeconds: number;
};

/**
 * Event payload shapes — these mirror the SDK's internal types
 * (`ClientEventMap[...]`) which are not exported from the package.
 */
export type TortusAvtConsultationStartedData = {
  consultation: TortusConsultation;
};

export type TortusAvtConsultationCompletedData = {
  consultation: TortusConsultationData | null;
  result: ConsultationOutput;
};

export type TortusAvtConsultationLifecycleData = {
  consultation: TortusConsultationData | null;
};

export type TortusAvtConsultationErrorData = {
  consultation: TortusConsultationData | null;
  error: TortusError;
};

export type TortusEmbedStatus = "idle" | "loading" | "ready" | "error";

export type TortusEmbedCallbacks = {
  /**
   * Called once the SDK is loaded and the iframe is in standby. Receives the
   * live TortusClient so callers can drive it (start consultations, navigate
   * screens, etc). The client is null'd out on teardown.
   */
  onClientReady?: (client: TortusClient | null) => Promisable<void>;
  onReady?: () => Promisable<void>;
  onConsultationStarted?: (
    data: TortusAvtConsultationStartedData,
  ) => Promisable<void>;
  onConsultationCompleted?: (
    data: TortusAvtConsultationCompletedData,
  ) => Promisable<void>;
  onConsultationCancelled?: (
    data: TortusAvtConsultationLifecycleData,
  ) => Promisable<void>;
  onConsultationClosed?: (
    data: TortusAvtConsultationLifecycleData,
  ) => Promisable<void>;
  onConsultationError?: (
    data: TortusAvtConsultationErrorData,
  ) => Promisable<void>;
  /**
   * Dictation lifecycle. The SDK's documented payload is just `{ event }`, but
   * we type these as `unknown` so callers can JSON-dump the raw runtime object
   * and inspect for any extra fields the types haven't yet caught up with.
   */
  onDictationStarted?: (data: unknown) => Promisable<void>;
  onDictationClosed?: (data: unknown) => Promisable<void>;
  /**
   * Convenience callback fired with the structured artifacts (medical notes,
   * codes, transcriptions) when a consultation completes — useful for
   * form-filling integrations.
   */
  onValues?: (values: TortusAvtConsultationCompletedData) => Promisable<void>;
  onError?: (err: unknown) => Promisable<void>;
};

/**
 * Owns the Tortus embed-client lifecycle for whichever shell renders it.
 *
 * SDK shape (from @tortus-ai/embed-client@1.5.0):
 *   loadTortus({ publishableKey, container, environment, fetchClientSecrets })
 *     → Promise<TortusClient>
 *   client.on(event, callback) → unsubscribe fn
 *   client.destroy() on teardown.
 *
 * Tearing down destroys the client, which ends any consultation in progress —
 * so a shell must keep this mounted for as long as the session should live.
 */
export function useTortusEmbed({
  open,
  tokenProvider,
  callbacks,
}: {
  open: boolean;
  tokenProvider: () => Promisable<TortusAvtLaunchToken>;
  callbacks: TortusEmbedCallbacks;
}): {
  containerId: string;
  status: TortusEmbedStatus;
  errorMessage: string | null;
} {
  const containerId = useId().replace(/:/g, "_");
  const clientRef = useRef<TortusClient | null>(null);
  const unsubscribesRef = useRef<Array<() => void>>([]);
  const [status, setStatus] = useState<TortusEmbedStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Hold the latest callback / provider refs so the init effect only depends on
  // `open`. Without this, every parent re-render that creates new inline
  // callbacks would tear down and re-init the SDK, which both costs a token
  // round-trip and risks infinite-update loops in Storybook.
  const tokenProviderRef = useRef(tokenProvider);
  const callbacksRef = useRef(callbacks);
  useEffect(() => {
    tokenProviderRef.current = tokenProvider;
    callbacksRef.current = callbacks;
  });

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setStatus("loading");
    setErrorMessage(null);

    const init = async () => {
      try {
        const selector = `#${CSS.escape(containerId)}`;
        // Shells portal their content and animate it in, so the container isn't
        // always in the DOM by the time this effect fires. Wait until it is —
        // up to a generous timeout.
        await waitForElement(selector, 2000);
        // The wait above runs for up to two seconds, so the shell may have closed
        // in the meantime. Bail before loadTortus rather than minting a launch
        // token for a session that no longer exists.
        if (cancelled) return;

        const client = await loadTortus({
          publishableKey: config.tortusAvt.publishableKey,
          container: selector,
          environment: config.tortusAvt.environment,
          fetchClientSecrets: async () => {
            const token = await tokenProviderRef.current();
            return { launchToken: token.launchToken };
          },
        });

        if (cancelled) {
          void client.destroy();
          return;
        }

        clientRef.current = client;

        unsubscribesRef.current.push(
          client.on(
            "consultation:started",
            (data: TortusAvtConsultationStartedData) => {
              void callbacksRef.current.onConsultationStarted?.(data);
            },
          ),
          client.on(
            "consultation:completed",
            async (
              data: TortusAvtConsultationCompletedData & {
                finish: (params?: {
                  status?: "success" | "failed";
                }) => Promise<void>;
              },
            ) => {
              void callbacksRef.current.onConsultationCompleted?.({
                consultation: data.consultation,
                result: data.result,
              });
              void callbacksRef.current.onValues?.({
                consultation: data.consultation,
                result: data.result,
              });
              // Acknowledge immediately so Tortus cleans up the session.
              // `finish` is not exposed to callers yet, so results are handled
              // from the callbacks above rather than gated on acknowledgement.
              //
              // Caught rather than left to reject: this runs inside an event
              // handler the SDK invokes, where a rejection would surface as an
              // unhandled one and bypass onError entirely. The results are
              // already delivered, so a failed acknowledgement is reportable
              // rather than fatal.
              try {
                await data.finish({ status: "success" });
              } catch (err) {
                void callbacksRef.current.onError?.(err);
              }
            },
          ),
          client.on(
            "consultation:cancelled",
            (data: TortusAvtConsultationLifecycleData) => {
              void callbacksRef.current.onConsultationCancelled?.(data);
            },
          ),
          client.on(
            "consultation:closed",
            (data: TortusAvtConsultationLifecycleData) => {
              void callbacksRef.current.onConsultationClosed?.(data);
            },
          ),
          client.on(
            "consultation:error",
            (data: TortusAvtConsultationErrorData) => {
              void callbacksRef.current.onConsultationError?.(data);
            },
          ),
          client.on("dictation:started", (data: unknown) => {
            void callbacksRef.current.onDictationStarted?.(data);
          }),
          client.on("dictation:closed", (data: unknown) => {
            void callbacksRef.current.onDictationClosed?.(data);
          }),
        );

        setStatus("ready");
        void callbacksRef.current.onReady?.();
        void callbacksRef.current.onClientReady?.(client);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setErrorMessage(message);
        setStatus("error");
        void callbacksRef.current.onError?.(err);
      }
    };

    void init();

    return () => {
      cancelled = true;
      for (const unsub of unsubscribesRef.current) {
        try {
          unsub();
        } catch {
          // ignore
        }
      }
      unsubscribesRef.current = [];
      const client = clientRef.current;
      clientRef.current = null;
      void callbacksRef.current.onClientReady?.(null);
      if (client) {
        void client.destroy();
      }
    };
  }, [open, containerId]);

  return { containerId, status, errorMessage };
}

export type TortusEmbedSurfaceProps = {
  containerId: string;
  status: TortusEmbedStatus;
  errorMessage: string | null;
  onClose: () => void;
  /**
   * Slot rendered in the header toolbar, to the left of the Close button. Use
   * this to surface controls that need to be clickable while the embed is open
   * (e.g. "Start consultation", "Go home").
   */
  headerActions?: ReactNode;
};

/**
 * The embed's chrome: header, load/error states, and the element the SDK mounts
 * its iframe into. Shell-agnostic — a dialog or a drawer supplies the framing
 * and the box it fills.
 */
export const TortusEmbedSurface = ({
  containerId,
  status,
  errorMessage,
  onClose,
  headerActions,
}: TortusEmbedSurfaceProps) => (
  <>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
        Tortus AVT
      </Typography>
      {headerActions}
      <Button size="small" onClick={onClose} data-testid="tortusAvtClose">
        Close
      </Button>
    </Box>

    {status === "loading" && (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    )}

    {status === "error" && (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Couldn&apos;t load Tortus AVT
          {errorMessage ? `: ${errorMessage}` : "."}
        </Alert>
      </Box>
    )}

    <Box
      id={containerId}
      data-testid="tortusAvtContainer"
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        "& > iframe": {
          flex: 1,
          width: "100%",
          height: "100%",
          border: 0,
        },
        visibility: status === "ready" ? "visible" : "hidden",
      }}
    />
  </>
);

const waitForElement = (selector: string, timeoutMs: number): Promise<void> =>
  new Promise((resolve, reject) => {
    const start = performance.now();
    const tick = () => {
      if (document.querySelector(selector)) {
        resolve();
        return;
      }
      if (performance.now() - start > timeoutMs) {
        reject(
          new Error(`Element not found after ${timeoutMs}ms: ${selector}`),
        );
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
